import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ALevelMenu from "./ALevelMenu";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import DashboardCard from "../../components/DashboardCard";
import WelcomeBanner from "../../components/WelcomeBanner";
import useAuthStore from "../../stores/useAuthStore";
import useApplicationStore from "../../stores/useApplicationStore";
import {
  FaUserFriends,
  FaUserGraduate,
  FaGraduationCap,
  FaImage,
  FaFileAlt,
  FaEnvelopeOpenText,
  FaMoneyBillWave,
  FaHandshake,
  FaClock,
  FaCheckCircle,
  FaHourglassHalf,
  FaCloudUploadAlt,
} from "react-icons/fa";

const _status = (data) => {
  switch (data) {
    case 0:
      return { text: "Pending", variant: "warning", icon: <FaHourglassHalf /> };
    case 1:
      return { text: "Provisional Admission", variant: "success", icon: <FaCheckCircle /> };
    case 2:
      return { text: "Conditional Admission", variant: "info", icon: <FaCheckCircle /> };
    default:
      return { text: "Processing", variant: "neutral", icon: <FaClock /> };
  }
};

const statusColors = {
  warning: "bg-warning-100 text-warning-700",
  success: "bg-success-100 text-success-700",
  info: "bg-cyan-100 text-cyan-700",
  neutral: "bg-neutral-100 text-neutral-600",
};

const Dashboard = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { setDashboard, getDashboard } = useApplicationStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboardDataset"],
    queryFn: async () => {
      const req = await axiosget("ms_dashboard");
      return req?.data;
    },
  });

  const { data: applications } = useQuery({
    queryKey: ["myApplications"],
    queryFn: async () => {
      const req = await axiosget("ms_applications");
      return req?.data;
    },
  });

  useEffect(() => {
    if (data && !data?.type) {
      setDashboard({
        bio: Boolean(data?.bio),
        ref: data?.ref >= 2,
        olevel: data?.olevel >= 1,
        result: data?.result >= 6,
        pay: Boolean(data?.pay),
        photo: Boolean(data?.photo),
      });
    }
  }, [data, setDashboard]);

  const getUserName = () => user?.firstname || "Student";

  const getNextStep = () => {
    return "/app/quick-apply";
  };

  const getQuickApplyLabel = () => "Quick Apply";

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50/30 pb-12">
      {isLoading && <Loader />}

      {error && (
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <Alert variant="danger"><AlertDescription>{`Local server error: ${error}`}</AlertDescription></Alert>
        </div>
      )}

      {data?.type !== undefined && (
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <Alert variant="danger"><AlertDescription>{`${data?.message}`}</AlertDescription></Alert>
        </div>
      )}

      {data && !data?.type && (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="px-4 sm:px-6 lg:px-8"
        >
          {/* Welcome Banner with Quick Apply */}
          <WelcomeBanner
            userName={getUserName()}
            onQuickApply={() => navigate(getNextStep())}
            quickApplyLabel={getQuickApplyLabel()}
          />

          {/* Row 1: 5 cards */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3"
          >
            <DashboardCard
              icon={<FaUserGraduate />}
              title="Bio Data"
              subtitle="Personal Information"
              onClick={() => navigate("/app/bio")}
              variant={data?.bio ? "success" : "danger"}
              completed={Boolean(data?.bio)}
              index={0}
            />
            <DashboardCard
              icon={<FaUserFriends />}
              title="Parents"
              subtitle="Guardians Info"
              onClick={() => navigate("/app/parents")}
              variant={data?.ref ? (data.ref >= 2 ? "success" : "info") : "danger"}
              completed={data?.ref >= 2}
              index={1}
            />
            <div className="relative">
              <ALevelMenu data={data} />
            </div>
            <DashboardCard
              icon={<FaGraduationCap />}
              title="A-Level"
              subtitle="Result Statement"
              onClick={() => navigate("/app/alevel")}
              variant="secondary"
              completed={false}
              index={3}
            />
            <DashboardCard
              icon={<FaImage />}
              title="Photo"
              subtitle="Passport Upload"
              onClick={() => navigate("/app/photo")}
              variant={data?.photo ? "success" : "danger"}
              completed={Boolean(data?.photo)}
              index={4}
            />
          </motion.div>

          {/* Row 2: 5 cards */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8"
          >
            <DashboardCard
              icon={<FaMoneyBillWave />}
              title="Payment"
              subtitle="Application Fee"
              onClick={() => navigate("/app/pay")}
              variant={data?.pay ? "success" : "danger"}
              completed={Boolean(data?.pay)}
              index={5}
            />
            <DashboardCard
              icon={<FaFileAlt />}
              title="Apply"
              subtitle="Submit Application"
              onClick={() => navigate("/app/start")}
              variant="danger"
              completed={false}
              index={6}
            />
            <DashboardCard
              icon={<FaEnvelopeOpenText />}
              title="Admission"
              subtitle="View Letters"
              onClick={() => navigate("/app/admission")}
              variant="info"
              completed={false}
              index={7}
            />
            <DashboardCard
              icon={<FaHandshake />}
              title="Acceptance"
              subtitle="Accept Offer"
              onClick={() => navigate("/app/accept")}
              variant="info"
              completed={false}
              index={8}
            />
            <DashboardCard
              icon={<FaCloudUploadAlt />}
              title="Credential Upload"
              subtitle="Upload Documents"
              onClick={() => navigate("/app/credentials")}
              variant={data?.pay ? "secondary" : "danger"}
              completed={false}
              index={9}
            />
          </motion.div>

          {/* My Applications Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h2 className="text-lg font-bold text-neutral-800 mb-4">My Applications</h2>

            {(!applications || applications?.type || (Array.isArray(applications) && applications.length === 0)) ? (
              <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
                <FaFileAlt className="text-3xl text-neutral-300 mx-auto mb-3" />
                <p className="text-neutral-500 text-sm">No applications submitted yet.</p>
                <p className="text-neutral-400 text-xs mt-1">Complete all required steps and apply to see your applications here.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {(Array.isArray(applications) ? applications : []).map((app, idx) => {
                  const status = _status(app?.status);
                  return (
                    <div
                      key={app?._id || idx}
                      className="bg-white rounded-xl border border-neutral-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-neutral-800 font-mono">{app?.code || "—"}</p>
                        <p className="text-xs text-neutral-500 truncate">
                          {app?.programme?.name || app?.programme?.code || "—"} &middot; {app?.session?.name || app?.session?.code || "—"}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[status.variant]}`}>
                          {status.icon}
                          {status.text}
                        </span>
                        {app?.createdAt && (
                          <span className="text-xs text-neutral-400 hidden sm:inline">
                            {new Date(app.createdAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Dashboard;
